/**
 * Copyright 2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect, useMemo, useState } from "react";
import { RowSelectionState } from "@tanstack/table-core";
import {
  GetEventsViewQuery,
  EventStatus,
  ExecutionStatus,
  useEventStatusChangedSubscription,
  useGetEventViewLazyQuery,
} from "~/generated/graphql";
import { useToggle } from "~/hooks";
import { isNotEmpty, sortItem } from "~/tools";
import { isFinishedEventStatus } from "~/views/events/helpers";
import { useLoadResourceStatesForAction } from "~/views/events/hooks";
import { useEventActionResourceCounts } from "~/views/events/hooks/useEventActionResourceCounts";
import { EventEntity } from "../../types";
import {
  buildErrorCodeCountsByActionId,
  getActionDescriptionForEntity,
  getActionErrorCodeCounts,
  getFlatActionEntities,
  getExpandedEventEntities,
} from "../EventItem.utils";

type Props = {
  event: NonNullable<GetEventsViewQuery["eventsView"]["result"]>[number];
};

export function useEventItem({ event }: Props) {
  const [getData, { data }] = useGetEventViewLazyQuery({
    variables: { id: event.id },
    fetchPolicy: "network-only",
  });

  const {
    updateResourceState,
    initializeActionCounts,
    clearAll,
    getCountsForAction,
  } = useEventActionResourceCounts();

  const loadResourceStatesForAction = useLoadResourceStatesForAction(
    event.id,
    initializeActionCounts
  );

  const [isExpanded, toggleIsExpanded] = useToggle();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [hasLoadedInitialStates, setHasLoadedInitialStates] = useState(false);

  const { data: eventStatusData } = useEventStatusChangedSubscription({
    variables: {
      input: {
        id: event.id,
      },
    },
    onData: getData,
  });

  const eventSource = data?.eventView ?? event;
  const liveEventStatus =
    eventStatusData?.eventStatusChanged ?? eventSource?.status;
  const isEventFinished = isFinishedEventStatus(liveEventStatus);
  const listActionEntities = useMemo(
    () => getFlatActionEntities(event),
    [event]
  );
  const flatActionEntities = useMemo(
    () => getFlatActionEntities(eventSource),
    [eventSource]
  );
  const eventStatusIconActions = useMemo(
    () =>
      eventSource?.status === EventStatus.Success
        ? listActionEntities
        : flatActionEntities,
    [eventSource?.status, flatActionEntities, listActionEntities]
  );
  const errorCodeCountsByActionId = useMemo(
    () => buildErrorCodeCountsByActionId(event),
    [event]
  );
  const allActionIds = useMemo(
    () => flatActionEntities.map((action) => action.id),
    [flatActionEntities]
  );

  useEffect(() => {
    return () => {
      clearAll();
    };
  }, [clearAll]);

  useEffect(() => {
    setHasLoadedInitialStates(false);
  }, [event.id]);

  useEffect(() => {
    if (
      !isExpanded ||
      hasLoadedInitialStates ||
      allActionIds.length === 0 ||
      isEventFinished
    ) {
      return;
    }

    void (async () => {
      await Promise.all(allActionIds.map(loadResourceStatesForAction));
      setHasLoadedInitialStates(true);
    })();
  }, [
    isExpanded,
    hasLoadedInitialStates,
    allActionIds,
    loadResourceStatesForAction,
  ]);

  const approveActions = event.actions?.filter(
    (eventAction) =>
      eventAction.globalState?.status === ExecutionStatus.ApprovalRequired
  );

  const approveBatches = event.actionBatches?.filter(
    (eventActionBatch) =>
      eventActionBatch.status === ExecutionStatus.ApprovalRequired
  );

  const eventEntities = getExpandedEventEntities(data?.eventView ?? event);
  const sortedEventEntities = sortItem(
    eventEntities,
    (a, b) => (a.order ?? 0) - (b.order ?? 0)
  );
  const hasEventEntities = isNotEmpty(listActionEntities);
  const isEventInProgress = liveEventStatus === EventStatus.InProgress;
  const actionLength =
    (approveActions?.length ?? 0) + (approveBatches?.length ?? 0);
  const getActionErrorCodeCountsForEntity = (
    eventEntity: Pick<EventEntity, "id" | "attempts">
  ) => getActionErrorCodeCounts(errorCodeCountsByActionId, eventEntity);
  const getActionDescription = (eventEntity: EventEntity) =>
    getActionDescriptionForEntity({
      errorCodeCountsByActionId,
      eventEntity,
      isEventFinished,
    });

  return {
    eventSource,
    liveEventStatus,
    isEventFinished,
    isEventInProgress,
    isExpanded,
    toggleIsExpanded,
    rowSelection,
    setRowSelection,
    hasEventEntities,
    actionLength,
    flatActionEntities,
    eventStatusIconActions,
    approveActions,
    approveBatches,
    sortedEventEntities,
    getData,
    getCountsForAction,
    updateResourceState,
    getActionErrorCodeCountsForEntity,
    getActionDescription,
  } as const;
}
